<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Account Settings</h1>
            <p class="text-gray-600">Manage your account and subscription</p>
          </div>
          <button @click="goBack" class="btn-tertiary">
            Back to Dashboard
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="space-y-8">
        <!-- Company Information -->
        <div class="bg-white rounded-lg shadow">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-medium text-gray-900">Company Information</h2>
            <p class="text-sm text-gray-600">Update your company details</p>
          </div>
          <div class="p-6">
            <form @submit.prevent="updateCompanyInfo" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="companyForm.company_name"
                  type="text"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  :class="{ 'border-red-500': errors.company_name }"
                />
                <p v-if="errors.company_name" class="mt-1 text-sm text-red-600">{{ errors.company_name }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="companyForm.email"
                  type="email"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  :class="{ 'border-red-500': errors.email }"
                />
                <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
              </div>
              <div class="flex justify-end">
                <button
                  type="submit"
                  :disabled="savingCompany"
                  class="btn-primary"
                >
                  <div v-if="savingCompany" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {{ savingCompany ? 'Saving...' : 'Update Company Info' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Subscription Information -->
        <div class="bg-white rounded-lg shadow">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-medium text-gray-900">Subscription Information</h2>
            <p class="text-sm text-gray-600">Your current subscription details</p>
          </div>
          <div class="p-6">
            <div v-if="loading" class="text-center py-4">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
              <p class="mt-2 text-gray-600">Loading subscription info...</p>
            </div>
            <div v-else-if="subscriptionInfo" class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Subscription Type</label>
                  <p class="mt-1 text-sm text-gray-900 capitalize">{{ subscriptionInfo.type }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Status</label>
                  <span
                    :class="[
                      'inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1',
                      subscriptionInfo.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : subscriptionInfo.status === 'expired'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    ]"
                  >
                    {{ subscriptionInfo.status.charAt(0).toUpperCase() + subscriptionInfo.status.slice(1) }}
                  </span>
                </div>
                <div v-if="subscriptionInfo.expires_at">
                  <label class="block text-sm font-medium text-gray-700">Expires</label>
                  <p class="mt-1 text-sm text-gray-900">{{ formatDate(subscriptionInfo.expires_at) }}</p>
                </div>
                <div v-if="subscriptionInfo.days_remaining !== null">
                  <label class="block text-sm font-medium text-gray-700">Days Remaining</label>
                  <p class="mt-1 text-sm text-gray-900">{{ subscriptionInfo.days_remaining }} days</p>
                </div>
              </div>
              
              <!-- Subscription Actions -->
              <div v-if="subscriptionInfo.status === 'expired'" class="mt-6 p-4 bg-yellow-50 rounded-lg">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                    </svg>
                  </div>
                  <div class="ml-3">
                    <h3 class="text-sm font-medium text-yellow-800">Subscription Expired</h3>
                    <div class="mt-2 text-sm text-yellow-700">
                      <p>Your subscription has expired. Contact support to reactivate your account.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Change Password -->
        <div class="bg-white rounded-lg shadow">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-medium text-gray-900">Change Password</h2>
            <p class="text-sm text-gray-600">Update your account password</p>
          </div>
          <div class="p-6">
            <form @submit.prevent="changePassword" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Current Password <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="passwordForm.current_password"
                  type="password"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  :class="{ 'border-red-500': errors.current_password }"
                />
                <p v-if="errors.current_password" class="mt-1 text-sm text-red-600">{{ errors.current_password }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  New Password <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="passwordForm.new_password"
                  type="password"
                  required
                  minlength="8"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  :class="{ 'border-red-500': errors.new_password }"
                />
                <p v-if="errors.new_password" class="mt-1 text-sm text-red-600">{{ errors.new_password }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="passwordForm.confirm_password"
                  type="password"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  :class="{ 'border-red-500': errors.confirm_password }"
                />
                <p v-if="errors.confirm_password" class="mt-1 text-sm text-red-600">{{ errors.confirm_password }}</p>
              </div>
              <div class="flex justify-end">
                <button
                  type="submit"
                  :disabled="savingPassword"
                  class="btn-primary"
                >
                  <div v-if="savingPassword" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {{ savingPassword ? 'Updating...' : 'Change Password' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Account Information -->
        <div class="bg-white rounded-lg shadow">
          <div class="px-6 py-4 border-b border-gray-200">
            <h2 class="text-lg font-medium text-gray-900">Account Information</h2>
            <p class="text-sm text-gray-600">Your account details and statistics</p>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700">Account ID</label>
                <p class="mt-1 text-sm text-gray-900">
                  <span v-if="loading">Loading...</span>
                  <span v-else>{{ clientInfo?.id || 'Not available' }}</span>
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Client Slug</label>
                <p class="mt-1 text-sm text-gray-900 font-mono">
                  <span v-if="loading">Loading...</span>
                  <span v-else>{{ clientInfo?.slug || 'Not available' }}</span>
                </p>
              </div>
                              <div>
                  <label class="block text-sm font-medium text-gray-700">Member Since</label>
                  <p class="mt-1 text-sm text-gray-900">
                    <span v-if="loading">Loading...</span>
                    <span v-else>{{ formatDate(clientInfo?.created_at) }}</span>
                  </p>
                </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Account Status</label>
                <span
                  v-if="clientInfo?.display_status"
                  :class="[
                    'inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1',
                    clientInfo.display_status.color === 'green' ? 'bg-green-100 text-green-800' :
                    clientInfo.display_status.color === 'red' ? 'bg-red-100 text-red-800' :
                    clientInfo.display_status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                    clientInfo.display_status.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  ]"
                  :title="clientInfo.display_status.description"
                >
                  {{ clientInfo.display_status.label }}
                </span>
                <span
                  v-else
                  :class="[
                    'inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1',
                    clientInfo?.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  ]"
                >
                  {{ clientInfo?.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import config from '../config.js'

// Router
const router = useRouter()

// Reactive data
const loading = ref(false)
const savingCompany = ref(false)
const savingPassword = ref(false)
const clientInfo = ref(null)
const subscriptionInfo = ref(null)
const errors = reactive({})

const companyForm = reactive({
  company_name: '',
  email: ''
})

const passwordForm = reactive({
  current_password: '',
  new_password: '',
  confirm_password: ''
})

// Methods
const loadClientInfo = async () => {
  try {
    loading.value = true
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/client`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('admin_token')
        router.push('/admin/login')
        return
      }
      if (response.status === 403) {
        // Subscription expired - show error but don't redirect
        const errorData = await response.json()
        console.error('Subscription error:', errorData.error)
        // Set subscription info if provided
        if (errorData.subscription_info) {
          subscriptionInfo.value = errorData.subscription_info
        }
        return
      }
      throw new Error('Failed to load client info')
    }

    const data = await response.json()
    clientInfo.value = data.client
    subscriptionInfo.value = data.client.subscription_info
    
    // Initialize company form
    companyForm.company_name = data.client.company_name || ''
    companyForm.email = data.client.email || ''
    
  } catch (error) {
    console.error('Error loading client info:', error)
  } finally {
    loading.value = false
  }
}

const updateCompanyInfo = async () => {
  try {
    savingCompany.value = true
    errors.company_name = ''
    errors.email = ''
    
    const token = localStorage.getItem('admin_token')
    if (!token) return

    const response = await fetch(`${config.apiBaseUrl}/admin/client`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        company_name: companyForm.company_name.trim(),
        email: companyForm.email.trim()
      })
    })

    if (response.ok) {
      // Update local state
      clientInfo.value.company_name = companyForm.company_name.trim()
      clientInfo.value.email = companyForm.email.trim()
      alert('Company information updated successfully!')
    } else {
      const error = await response.json()
      if (error.error) {
        if (error.error.includes('email')) {
          errors.email = error.error
        } else {
          errors.company_name = error.error
        }
      }
    }
  } catch (error) {
    console.error('Error updating company info:', error)
    alert('Error updating company information. Please try again.')
  } finally {
    savingCompany.value = false
  }
}

const changePassword = async () => {
  try {
    savingPassword.value = true
    errors.current_password = ''
    errors.new_password = ''
    errors.confirm_password = ''
    
    // Validate passwords match
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      errors.confirm_password = 'Passwords do not match'
      return
    }
    
    // Validate password length
    if (passwordForm.new_password.length < 8) {
      errors.new_password = 'Password must be at least 8 characters long'
      return
    }

    const token = localStorage.getItem('admin_token')
    if (!token) return

    const response = await fetch(`${config.apiBaseUrl}/admin/client`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        current_password: passwordForm.current_password,
        password: passwordForm.new_password
      })
    })

    if (response.ok) {
      // Clear form
      passwordForm.current_password = ''
      passwordForm.new_password = ''
      passwordForm.confirm_password = ''
      alert('Password updated successfully!')
    } else {
      const error = await response.json()
      if (error.error) {
        if (error.error.includes('current password')) {
          errors.current_password = error.error
        } else {
          errors.new_password = error.error
        }
      }
    }
  } catch (error) {
    console.error('Error changing password:', error)
    alert('Error changing password. Please try again.')
  } finally {
    savingPassword.value = false
  }
}

const goBack = () => {
  router.push('/admin/dashboard')
}

const formatDate = (dateString) => {
  if (!dateString) return 'Not available'
  try {
    return new Date(dateString).toLocaleDateString()
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid date'
  }
}

// Lifecycle
onMounted(() => {
  loadClientInfo()
})
</script> 